import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useInspectionId } from "../hooks/useInspectionId";
import { useAppDispatch, useAppSelector } from "../hooks/storeHooks";
import { resetFilters, setSurveyorAnswers } from "../store/slices/answerSlice";
import { fetchReviewAnswers, fetchSelectedInspection, fetchSurveyorAnswers } from "../store/thunks/answerThunks";
import { triggerEmissionsCalculation } from "../services/answerService";
import { generateReport, getReportStatus } from "../services/reportService";
import { errorToast } from "@idwal/idwal-react-components";
import { selectAnswer, selectSelectedInspection } from "../store/selectors/answerSelectors";
import { ProgressSpinner } from "primereact/progressspinner";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { TabPanel, TabView, TabViewTabChangeEvent } from "primereact/tabview";
import { AnswerTable } from "../components/Answer/AnswerTable";
import { SurveyorSubmissionTable } from "../components/Answer/SurveyorSubmissionTable";
import { QuestionImagesGalleria } from "../components/Answer/QuestionImagesGalleria";

// Reporting is offered only on the config-driven processing path: that report
// is rendered wholly from our own data plane (inspection record, answers and
// checklist config). An inspection on the established path has its report
// produced elsewhere, so the trigger has no place on its review page.
const CONFIG_DRIVEN_PIPELINE = "config-driven";

// Report generation is asynchronous: trigger -> poll the control-plane -> open.
type ReportRequestState = "idle" | "generating" | "ready" | "error";
const REPORT_POLL_BASE_MS = 3000;
const REPORT_POLL_MAX_MS = 8000;
const REPORT_POLL_DEADLINE_MS = 90000;

// Only ever hand the browser an http(s) report URL (defence-in-depth on the API response).
const isHttpUrl = (value: string): boolean => {
    try {
        const { protocol } = new URL(value);
        return protocol === "http:" || protocol === "https:";
    } catch {
        return false;
    }
};

const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString("en-GB") : null;

const formatDateTime = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    return `${date.toLocaleDateString("en-GB")} at ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};

const Detail = ({ label, value, prominent }: { label: string; value?: string | null; prominent?: boolean }) => (
    <div className="flex gap-3">
        <span className="inspection-info__label">{label}</span>
        <span className={`inspection-info__value${prominent ? " inspection-info__value--prominent" : ""}`}>{value || "—"}</span>
    </div>
);

const Answers = () => {
    const { t } = useTranslation("locale");
    const inspectionId = useInspectionId();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { loading, loadingInspections, error, surveyorError } = useAppSelector(selectAnswer);
    const selectedInspection = useAppSelector(selectSelectedInspection);
    const [activeTab, setActiveTab] = useState(0);
    const [calculating, setCalculating] = useState(false);
    const [reportState, setReportState] = useState<ReportRequestState>("idle");
    const [reportUrl, setReportUrl] = useState<string | null>(null);
    const surveyorFetchedFor = useRef<string | null>(null);
    const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pollActive = useRef(false);

    // Stop any in-flight poll (unmount, inspection change, or a terminal state).
    const stopPolling = useCallback(() => {
        pollActive.current = false;
        if (pollTimer.current) {
            clearTimeout(pollTimer.current);
            pollTimer.current = null;
        }
    }, []);

    useEffect(() => stopPolling, [stopPolling]);

    // Resolve the inspection directly by id (the list endpoint only returns a
    // single 50-item scan page, so it can't be relied on for deep-links), then
    // load its review answers (the editable tech-review version). The active
    // tab resets so a newly opened inspection always starts on the editable
    // view; the previous inspection's surveyor copy is dropped because that
    // tab lazy-loads on first visit.
    useEffect(() => {
        if (inspectionId) {
            stopPolling();
            setReportState("idle");
            dispatch(resetFilters());
            setActiveTab(0);
            setReportUrl(null);
            dispatch(setSurveyorAnswers([]));
            dispatch(fetchSelectedInspection(inspectionId));
            dispatch(fetchReviewAnswers(inspectionId));
        }
    }, [dispatch, inspectionId, stopPolling]);

    // The surveyor submission is fetched only when its tab is first opened
    // (or re-opened after a failed fetch)
    const onTabChange = (event: TabViewTabChangeEvent) => {
        setActiveTab(event.index);
        if (event.index === 1 && inspectionId
            && (surveyorFetchedFor.current !== inspectionId || surveyorError)) {
            surveyorFetchedFor.current = inspectionId;
            dispatch(fetchSurveyorAnswers(inspectionId));
        }
    };

    const handleBackClick = () => {
        navigate("/inspection/answers");
    };

    // No local feedback handling — the service toasts success/failure itself.
    const handleCalculateEmissions = async () => {
        if (!inspectionId || calculating) return;
        setCalculating(true);
        try {
            await triggerEmissionsCalculation(inspectionId);
        } finally {
            setCalculating(false);
        }
    };

    if (loading || loadingInspections) {
        return (
            <div className="flex h-full align-items-center justify-content-center">
                <ProgressSpinner />
            </div>
        );
    }

    if (!selectedInspection) {
        return (
            <div className="flex flex-column align-items-center justify-content-center gap-3 p-5">
                <p>{t("answers.notFound")}</p>
                <Button label={t("answers.backToInspections")} icon="pi pi-arrow-left" onClick={handleBackClick} />
            </div>
        );
    }

    const inspection = selectedInspection as Record<string, unknown>;
    const inspectionNumber = inspection.inspectionNumber as string | null;
    const vesselName = inspection.vesselName as string | null;
    const vesselId = inspection.vesselId as string | null;
    const shipType = inspection.shipType as string | null;
    const location = inspection.location as string | null;
    const inspectionDate = inspection.inspectionDate as string | null;
    const checklistType = inspection.checklistType as string | null;
    const checklistVersion = inspection.checklistVersion as string | null;
    const surveyorName = inspection.surveyorName as string | null;
    const submittedAt = (inspection.submittedAt ?? inspection.updatedAt) as string | null;
    const submissionTimestamp = (inspection.submittedAt ?? null) as string | null;

    const versionSuffix = checklistVersion ? `, v${checklistVersion}` : "";
    const checklistLabel = checklistType ? `${checklistType}${versionSuffix}` : null;

    const status = inspection.status as string | null;
    const isConfigDriven = inspection.pipeline === CONFIG_DRIVEN_PIPELINE;
    const canCalculate = !!inspectionNumber && status !== "cancelled" && status !== "deleted";
    const calculateButton = (
        <Button
            label={t("answers.calculateEmissions.button")}
            loading={calculating}
            disabled={!canCalculate}
            onClick={handleCalculateEmissions}
        />
    );

    // Trigger the async report build, then poll the control-plane until the PDF
    // is ready. Clicking again while generating is a no-op; clicking after an
    // error retries. Nothing here touches ASIMS — status and URL come only from
    // the report control-plane.
    const handleGenerateReport = async () => {
        if (!inspectionId || reportState === "generating") return;

        stopPolling();
        setReportUrl(null);
        setReportState("generating");

        const triggered = await generateReport(inspectionId);
        if (!triggered) {
            // generateReport already toasted the failure.
            setReportState("error");
            return;
        }
        if (triggered.status === "error") {
            setReportState("error");
            errorToast(t("answers.reportError"));
            return;
        }

        pollActive.current = true;
        const deadline = Date.now() + REPORT_POLL_DEADLINE_MS;
        let intervalMs = REPORT_POLL_BASE_MS;

        const poll = async () => {
            if (!pollActive.current) return;
            const report = await getReportStatus(inspectionId);
            if (!pollActive.current) return;

            if (report?.status === "ready" && report.url) {
                stopPolling();
                if (!isHttpUrl(report.url)) {
                    setReportState("error");
                    errorToast(t("answers.reportError"));
                    return;
                }
                setReportUrl(report.url);
                setReportState("ready");
                // Best-effort: after the await this is outside the click gesture so
                // the browser may block it — the "View report" link is the reliable
                // path, this just saves a click when it isn't blocked.
                window.open(report.url, "_blank", "noopener,noreferrer");
                return;
            }
            if (report?.status === "error") {
                stopPolling();
                setReportState("error");
                errorToast(t("answers.reportError"));
                return;
            }
            if (Date.now() >= deadline) {
                stopPolling();
                setReportState("error");
                errorToast(t("answers.reportTimeout"));
                return;
            }

            // Gentle backoff, capped, so a slow render doesn't hammer the API.
            intervalMs = Math.min(Math.round(intervalMs * 1.2), REPORT_POLL_MAX_MS);
            pollTimer.current = setTimeout(poll, intervalMs);
        };

        pollTimer.current = setTimeout(poll, intervalMs);
    };

    return (
        <div className="flex flex-column gap-3">
            <div className="flex align-items-center justify-content-between gap-3">
                <h1 className="checklist-review-title m-0">{t("answers.title")}</h1>
                {isConfigDriven && (
                    <div className="flex align-items-center gap-2">
                        {reportState === "error" && (
                            <span className="text-sm text-red-500">{t("answers.reportError")}</span>
                        )}
                        {reportState === "ready" && reportUrl && (
                            <a
                                href={reportUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-button p-button-outlined p-button-sm no-underline"
                            >
                                <span className="pi pi-external-link mr-2" />
                                {t("answers.viewReport")}
                            </a>
                        )}
                        <Button
                            label={reportState === "error" ? t("answers.retryReport") : t("answers.generateReport")}
                            icon="pi pi-file-pdf"
                            loading={reportState === "generating"}
                            disabled={reportState === "generating"}
                            onClick={handleGenerateReport}
                        />
                    </div>
                )}
            </div>

            <Card>
                <div className="inspection-info">
                    <div className="inspection-info__col flex-1 flex flex-column gap-2">
                        <Detail label={t("answers.details.vesselName")} value={vesselName} prominent />
                        <Detail label={t("answers.details.imoNumber")} value={vesselId} />
                        <Detail label={t("answers.details.vesselType")} value={shipType} />
                    </div>
                    <div className="inspection-info__divider" />
                    <div className="inspection-info__col flex-1 flex flex-column gap-2">
                        <Detail label={t("answers.details.checklist")} value={checklistLabel} prominent />
                        <Detail label={t("answers.details.location")} value={location} />
                        <Detail label={t("answers.details.inspectionDate")} value={formatDate(inspectionDate)} />
                    </div>
                    <div className="inspection-info__divider" />
                    <div className="inspection-info__col flex-1 flex flex-column gap-2">
                        <Detail label={t("answers.details.reference")} value={inspectionNumber} prominent />
                        <Detail label={t("answers.details.submitted")} value={formatDateTime(submittedAt)} />
                        <Detail label={t("answers.details.surveyor")} value={surveyorName} />
                    </div>
                </div>
            </Card>

            <TabView activeIndex={activeTab} onTabChange={onTabChange}>
                <TabPanel header={t("answers.tabs.editableChecklist")}>
                    <Card>
                        {error ? (
                            <div className="flex align-items-center justify-content-center p-5">
                                <p>{t("answers.loadError")}</p>
                            </div>
                        ) : (
                            <AnswerTable headerActions={calculateButton} />
                        )}
                    </Card>
                </TabPanel>
                <TabPanel header={t("answers.tabs.surveyorSubmission")}>
                    <Card>
                        <SurveyorSubmissionTable submittedAt={submissionTimestamp} />
                    </Card>
                </TabPanel>
            </TabView>

            <QuestionImagesGalleria />
        </div>
    );
};

export default Answers;
